import { observable }         from 'mobx';
import { makeObservable }     from 'mobx';
import { action }             from 'mobx';
import { computed }           from 'mobx';
import listFilterManagerStore from '@widesk-ui/stores/ListFilterManagerStore';
import { ListProps }          from '@widesk-ui/components/List';
import SplitViewStore         from '@widesk-ui/stores/SplitViewStore';

export default class ListStore {
	@observable private _error: any = false;
	@observable private _isLoading = true;
	@observable private _data = [];
	@observable private _currentIndex = 0;
	@observable private _manualCurrentIndex?: number;
	@observable private _splitViewStore?: SplitViewStore;

	private _checkboxListIndex = observable.array<number>([]);

	private _props: ListProps<any>;

	public constructor(props: ListProps<any>) {
		this._props = props;

		if (props.storageKey) {
			const filters = this.filters;
			listFilterManagerStore.set(props.storageKey, filters, props.urlStorageEnabled);

			const index = parseInt(filters.idx as string);
			this._currentIndex = (index > 0 && index <= this.pageSize - 1) ? index : 0;
		}

		makeObservable(this);
	}

	public setProps(props: ListProps<any>) {
		this._props = props;
	}

	@action setIsLoading(value: boolean) {
		this._isLoading = value;
	}

	@action setError(value: any) {
		this._error = value;
	}

	@action setData(data: []) {
		this._data = data;
	}

	@action setCurrentIndex(index: number, manual?: boolean) {
		this._currentIndex = index;

		if (manual) {
			this._manualCurrentIndex = index;
		}

		if (this._props.storageKey) {
			listFilterManagerStore.set(
				this._props.storageKey,
				{ ...this.filters, idx: index },
				this._props.urlStorageEnabled,
			);
		}
	}

	@action setManualCurrentIndex(manualCurrentIndex?: number) {
		this._manualCurrentIndex = manualCurrentIndex;
	}

	@action toggleCheckbox(index: number) {
		const newIndex = this._checkboxListIndex.indexOf(index);

		if (newIndex > -1) {
			const n = [...this._checkboxListIndex];
			n.splice(newIndex, 1);
			this._checkboxListIndex.replace(n);
		} else {
			this._checkboxListIndex.push(index);
		}
	}

	@action checkAllCheckbox() {
		this._checkboxListIndex.replace(this._data.map((_, idx) => idx));
	}

	@action clearCheckboxList() {
		this._checkboxListIndex.clear();
	}

	@action setSplitViewStore(store?: SplitViewStore) {
		this._splitViewStore = store;
	}

	@computed get splitViewStore() {
		return this._splitViewStore;
	}

	@computed get currentIndex() {
		return this._currentIndex;
	}

	@computed get manualCurrentIndex() {
		return this._manualCurrentIndex;
	}

	@computed get checkboxListIndex() {
		return this._checkboxListIndex;
	}

	@computed get checkboxListData() {
		return this._checkboxListIndex.map(idx => {
			return this._data[idx];
		});
	}

	@computed get isLoading() {
		return this._isLoading;
	}

	@computed get error() {
		return this._error;
	}

	@computed get data() {
		return this._data;
	}

	@computed get currentItem() {
		return this._data[this._currentIndex];
	}

	@computed get totalPages() {
		return Math.ceil(this.total / this.pageSize);
	}

	public get props() {
		return this._props;
	}

	public reload() {
		return this.fetch(this.page, this.pageSize, this.filters);
	}

	public fetchPage(page: number, idx = 0) {
		return this.fetch(page, this.pageSize, { ...this.filters, idx });
	}

	public async fetch(page: number, step: number, filters: Record<string, unknown>) {
		try {
			this.setIsLoading(true);
			this.setError(false);
			this.clearCheckboxList();

			const data = this.props.dataSource || await this._props.fetch!(page, step, filters);

			if (this._props.storageKey) {
				listFilterManagerStore.set(
					this._props.storageKey,
					{ idx: this._currentIndex, ...filters, page, step },
					this._props.urlStorageEnabled,
				);
			}

			this.setData(data);
			this.setCurrentIndex(parseInt(this.filters.idx as string) || 0);
			this.setManualCurrentIndex(undefined);

			if (this._props.onFetchSuccess) {
				this._props.onFetchSuccess();
			}

			return data;
		} catch (e) {
			this.setError(e);

			throw e;
		} finally {
			this.setIsLoading(false);
		}
	}

	public fetchFilters = (filters: Record<string, unknown>) => this.fetch(1, this.pageSize, { idx: 0, ...filters });

	public get filters() {
		// Chargement des filtres dans le localStorage
		const filters = this._props.storageKey ?
			listFilterManagerStore.get(this._props.storageKey, this._props.urlStorageEnabled) : {};

		// S'il n'y a pas de filtre
		if (!Object.keys(filters).filter(key => !['page', 'step'].includes(key)).length) {
			// On utilise les filtres par défaut
			return this._props.defaultFilters || {};
		}

		return filters;
	}

	public get page() {
		return parseInt(this.filters.page as string) || 1;
	}

	public get pageSize() {
		return (this._props.pagination && this._props.pagination?.pageSize) || this.filters.step as number || 20;
	}

	public get total() {
		const pagination = (this._props.pagination as any);
		if (pagination?.pagedCollection) {
			return pagination.pagedCollection.total || 0;
		}

		return 0;
	}

	public get globalCurrentIndex() {
		return ((this.page - 1) * this.pageSize) + (this._currentIndex + 1);
	}

	public get nextDisabled() {
		return (
			this.page >= (this.total / this.pageSize)
			&& this.currentIndex + 1 >= this.data.length
		);
	}

	public get prevDisabled() {
		return this.page === 1 && this.currentIndex === 0;
	}

	public get currentOnTotal() {
		return `${this.globalCurrentIndex} sur ${this.total}`;
	}

	public async prev() {
		const currentIndex = this.currentIndex;

		if (currentIndex - 1 < 0) {
			return this.prevPage();
		} else {
			this.setCurrentIndex(currentIndex - 1);
			this.setManualCurrentIndex(undefined);
		}
	}

	public async next() {
		const currentIndex = this.currentIndex;
		const pageSize = this.pageSize;

		if (currentIndex + 1 >= pageSize) {
			return this.nextPage();
		} else {
			this.setCurrentIndex(currentIndex + 1);
			this.setManualCurrentIndex(undefined);
		}
	}

	public async prevPage() {
		if (this.page > 1) {
			return this.fetchPage(this.page - 1, this.pageSize - 1);
		}
	}

	public async nextPage() {
		if (this.page >= this.totalPages) {
			return;
		}
		return this.fetchPage(this.page + 1);
	}
}